import { ComponentType } from '@angular/cdk/portal';
import {
  ComponentRef,
  Inject,
  Injectable,
  Injector,
  NgModuleFactory,
  NgModuleFactoryLoader,
  Optional,
  SkipSelf,
  ViewContainerRef,
} from '@angular/core';
import { ROUTES, Routes } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

/**
 * Custom injector to be used when providing custom
 * injection tokens to components inside a portal.
 */
export class PortalInjector implements Injector {
  constructor(
    private _parentInjector: Injector,
    private _customTokens: WeakMap<any, any>,
  ) {}

  get(token: any, notFoundValue?: any): any {
    const value = this._customTokens.get(token);

    if (typeof value !== 'undefined') {
      return value;
    }

    return this._parentInjector.get<any>(token, notFoundValue);
  }
}

export interface ModalConfig {
  inputs?: Object; // the @Input() values to pass to the component
  // height: number; ??
  // width: number; ??
}

export type ModalResult = any;

export class ModalRef<T> {
  /** Subject for notifying the user that the dialog has finished closing. */
  private _afterClosed: Subject<ModalResult> = new Subject();
  private componentRef: ComponentRef<T>;

  set(componentRef: ComponentRef<T>) {
    this.componentRef = componentRef;
  }

  close(result?: ModalResult): void {
    this.componentRef.destroy();
    this._afterClosed.next(result);
  }

  afterClosed(): Observable<ModalResult> {
    return this._afterClosed.asObservable();
  }
}

@Injectable()
export class ModalService {
  private openStreamSubject = new Subject<any>();
  openStream = this.openStreamSubject.asObservable();

  // rendering container
  private injector: Injector;
  private viewContainerRef: ViewContainerRef;

  constructor(
    private loader: NgModuleFactoryLoader,
    @Inject(ROUTES) private readonly loadableRoutes: Routes[],
  ) {}

  open<T>(
    component: ComponentType<T>,
    config: ModalConfig = {},
  ): Promise<ModalRef<any>> {
    const ngModule = this.getModalNgModule(component);
    return this.loader
      .load(ngModule)
      .then((ngModuleFactory: NgModuleFactory<any>) => {
        // get component factory
        const componentFactory = ngModuleFactory
          .create(this.injector) // ngModuleRef
          .componentFactoryResolver.resolveComponentFactory(component);

        // create custom injector
        const injectionTokens = new WeakMap();
        const modalRef = new ModalRef();
        injectionTokens.set(ModalRef, modalRef);
        const customInjector = new PortalInjector(
          this.injector,
          injectionTokens,
        );

        // create component
        const componentRef = this.viewContainerRef.createComponent(
          componentFactory,
          0,
          customInjector,
        );
        Object.entries(config.inputs).forEach(([key, value]) => {
          // FIXME: ComponentType<T> has no index signature.
          (<any>componentRef).instance[key] = value;
        });
        modalRef.set(componentRef);

        return modalRef;
      });
  }

  setContainer(viewContainerRef: ViewContainerRef, injector: Injector) {
    this.viewContainerRef = viewContainerRef;
    this.injector = injector;
  }

  private getModalNgModule<T>(component: ComponentType<T>): string {
    for (let i = 0; i < this.loadableRoutes.length; ++i) {
      const routeList = this.loadableRoutes[i];
      for (let j = 0; j < routeList.length; ++j) {
        const route = routeList[j];
        if (
          typeof route.loadChildren === 'string' &&
          route.component === component
        ) {
          return route.loadChildren;
        }
      }
    }
    throw new Error(`missing modal module configuration for ${component.name}`);
  }
}

/**
 * Provider factory for Modal Service
 *
 * @export
 * @param {ModalService} parentRegistry The parent registry
 * @returns {ModalService} The singleton instance or a new one
 */
export function MODAL_SERVICE_PROVIDER_FACTORY(
  parentRegistry: ModalService,
  loader: NgModuleFactoryLoader,
  loadableRoutes: Routes[],
) {
  return parentRegistry || new ModalService(loader, loadableRoutes);
}

/**
 * Create a service provider that acts as a singleton.
 */
export const MODAL_SERVICE_PROVIDER = {
  // If there is already an ModalService available, use that.
  // Otherwise, provide a new one.
  provide: ModalService,
  deps: [
    [new Optional(), new SkipSelf(), ModalService],
    NgModuleFactoryLoader,
    ROUTES,
  ],
  useFactory: MODAL_SERVICE_PROVIDER_FACTORY,
};



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/ui/modal/modal.service.ts